import { DragEvent, RefObject } from 'react'
import { CancelSecondaryIcon, CsvColorIcon, DownloadBlueIcon, ExcelColorIcon, TickBlueIcon, UploadFileIconBlue } from '../../icon'
import ButtonComponent from '../shared/button/button.component'
import { buttonLabels } from '../shared/constants'
import {
    ImportColumnChip,
    ImportInfoBanner,
    ImportStepCard,
    ImportStepFooter,
    ImportStepLayout,
    NUMBER_IMPORT_COLUMNS
} from './import-numbers-shared.component'

const NUMBER_IMPORT_RULES = [
    "Name is required for every row",
    "Phone Number must be in E.164 — +14155550142",
    "Numbers already hosted for that organization are skipped",
    "Organization and address from the previous step apply to every row"
]

interface IProps {
    IsDarkMode: boolean
    UploadedFile?: File
    RowCount: number
    SheetCount: number
    FileUploadError: string
    FileInputRef: RefObject<HTMLInputElement | null>
    OrganizationName: string
    AddressSummary: string
    OnFileUpload: (file: File | undefined) => void
    OnClearFile: () => void
    OnBack: () => void
    OnValidate: () => void
}

const formatFileSizeMb = (size: number) => `${(size / (1024 * 1024)).toFixed(1)} MB`

const UploadFileStep = (props: IProps) => {
    const hasFile = !!props.UploadedFile?.name
    const isCsv = props.UploadedFile?.name.toLowerCase().endsWith(".csv")

    const handleOpenFilePicker = () => {
        props.FileInputRef.current?.click()
    }

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        const file = event.dataTransfer.files?.[0]
        if (file) {
            props.OnFileUpload(file)
        }
    }

    return (
        <ImportStepLayout
            isDarkMode={props.IsDarkMode}
            left={
                <>
                    <ImportInfoBanner title="Two columns: Name and Phone Number">
                        Headers must read Name then Phone Number, in that order — we read the sheet directly, with no mapping step.
                    </ImportInfoBanner>

                    <div style={{ backgroundColor: "#f7faff" }} className=' d-flex flex-direction-row b-around-new-bulk-import-step-card-border justify-between mt-3 py-3 px-2 br-3'>
                        <div className='d-flex flex-direction-row align-start justify-start'>
                            <div className='d-flex flex-direction align-start justify-start mr-2'>
                                <div className='h5-regular color-primary-accent'>Hosting into</div>
                                <div className='h4-semibold color-primary-accent'>{props.OrganizationName || "—"}</div>
                            </div>
                            <div className='vertical-line br-1' style={{
                                height: "100%",
                                color: "#d6e4ff",
                                border: "1px solid #d6e4ff",
                                backgroundColor: "#d6e4ff",
                            }}></div>
                            <div className='d-flex flex-direction align-start justify-start ml-2'>
                                <div className='h5-regular color-primary-accent'>Address</div>
                                <div className='h4-semibold color-primary-accent'>{props.AddressSummary || "—"}</div>
                            </div>
                        </div>

                        <button
                            type="button"
                            className='c-pointer color-primary-accent d-flex flex-direction-row justify-end align-center'
                            onClick={props.OnBack}
                        >
                            <div className='h4-semibold color-primary-btn'>Change</div>
                        </button>

                    </div>

                    {!hasFile ? (
                        <div
                            className='d-flex flex-direction align-center justify-center mt-3 py-3 px-2 br-3 b-around-new-users-dropzone-border c-pointer'
                            onDrop={handleDrop}
                            onDragOver={(event) => event.preventDefault()}
                            onClick={handleOpenFilePicker}
                        >
                            <div className='p-2 bg-color-primary-light-blue br-2 mb-2'>
                                <UploadFileIconBlue />
                            </div>
                            <div className='h2-medium color-primary-accent t-align-center'>
                                Drop your Excel file here, or <span className='color-primary-light'>browse</span>
                            </div>
                            <div className='h5-regular color-text-field-label mt-1 t-align-center'>
                                .xlsx, .xls or .csv · up to 500 numbers per file · max 10 MB
                            </div>
                            {!!props.FileUploadError && (
                                <div className='h5-regular color-primary-danger mt-1 t-align-center'>{props.FileUploadError}</div>
                            )}
                        </div>
                    ) : (
                        <div
                            className='d-flex align-center justify-between mt-3 p-2 br-3 b-around-new-users-step-card-border'
                            onDrop={handleDrop}
                            onDragOver={(event) => event.preventDefault()}
                        >
                            <div className='d-flex align-center'>
                                <div
                                    className='d-flex align-center justify-center br-2 flex-shrink-0'
                                    style={{ width: 40, height: 40, backgroundColor: '#FFF3E8' }}
                                >
                                    {isCsv ? <CsvColorIcon className='w-3 h-3' /> : <ExcelColorIcon />}
                                </div>
                                <div className='ml-1'>
                                    <div className='h4-medium color-primary-accent w-break'>{props.UploadedFile?.name}</div>
                                    <div className='h5-regular color-text-field-label'>
                                        {formatFileSizeMb(props.UploadedFile?.size || 0)} · Sheet 1 of {props.SheetCount || 1} · uploaded just now
                                    </div>
                                </div>
                            </div>
                            <div className='d-flex align-center flex-shrink-0 ml-1'>
                                <div
                                    className='h5-medium px-1 py-1 br-5 w-space'
                                    style={{ backgroundColor: '#E8F6ED', color: '#157A3A' }}
                                >
                                    ✓ {props.RowCount} rows read
                                </div>
                                <button
                                    type="button"
                                    className='ml-1 color-text-field-label d-flex align-center'
                                    onClick={props.OnClearFile}
                                >
                                    <CancelSecondaryIcon className='w-2 h-2' />
                                </button>
                            </div>
                        </div>
                    )}
                    <input
                        accept='.csv,.xls,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
                        ref={props.FileInputRef}
                        type="file"
                        hidden
                        onChange={(e) => props.OnFileUpload(e?.target?.files?.[0])}
                    />

                    <ImportStepFooter
                        backLabel={buttonLabels.BACK}
                        onBack={props.OnBack}
                        continueLabel={hasFile ? `Validate ${props.RowCount} rows →` : "Validate rows →"}
                        onContinue={props.OnValidate}
                        canContinue={hasFile && !props.FileUploadError}
                    />
                </>
            }
            right={
                <>
                    <ImportStepCard isDarkMode={props.IsDarkMode} className="mb-2">
                        <div className='h3-medium color-primary-accent'>Start from the template</div>
                        <div className='h5-regular color-text-field-label mt-1'>
                            One number per row. Always start here — a modified sheet is rejected on upload.
                        </div>
                        <ButtonComponent
                            ButtonLabel="Download Excel template"
                            Variant="secondary-btn"
                            Icon={DownloadBlueIcon}
                            ClassName="m-0 mt-2 pt-2 pb-2 br-3"
                            buttonProps={{
                                style: {
                                    textTransform: 'none',
                                    fontFamily: 'silkamedium',
                                    fontSize: '1rem',
                                    border: '1px solid var(--color-primary-light)',
                                    color: 'var(--color-primary-light)'
                                }
                            }}
                            OnClick={() => { }}
                        />
                    </ImportStepCard>
                    <ImportStepCard isDarkMode={props.IsDarkMode} className="mb-2">
                        <div className='h3-medium color-primary-accent'>Required columns, in this order</div>
                        <div className='d-flex flex-wrap mt-2'>
                            {NUMBER_IMPORT_COLUMNS.map((column) => (
                                <ImportColumnChip
                                    key={column.label}
                                    label={column.label}
                                    required={column.required}
                                    columnLetter={column.letter}
                                />
                            ))}
                        </div>
                    </ImportStepCard>
                    <ImportStepCard isDarkMode={props.IsDarkMode}>
                        <div className='h3-medium color-primary-accent'>Rules we enforce</div>
                        <div className='mt-2'>
                            {NUMBER_IMPORT_RULES.map((rule) => (
                                <div key={rule} className='d-flex align-start mb-1'>
                                    <TickBlueIcon className='flex-shrink-0 mt-1' />
                                    <div className='h5-regular color-text-field-label ml-1'>{rule}</div>
                                </div>
                            ))}
                        </div>
                    </ImportStepCard>
                </>
            }
        />
    )
}

export default UploadFileStep
